import React from "react";

import { ValidationIssue } from "../../../rule-engine-core/Common/IDesignValidationProvider";
import { IDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/IDataDefinition";

import { StyledButton } from "../../../CommonUI/BuildlingBlocks/StyledButton";
import { IconButton } from "@mui/material";
import { PropertyEditorText } from "../../../CommonUI/PropertyEditing/PropertyEditorText";
import { PropertyEditorSelect } from "../../../CommonUI/PropertyEditing/PropertyEditorSelect";
import { PropertyEditorImage } from "../../../CommonUI/PropertyEditing/PropertyEditorImage";
import { ButtonControlSize } from "../../Utilities/FormDesignConstants";
import { StyledLink } from "../../../CommonUI/BuildlingBlocks/StyledLink";
import { FormControl } from "../FormControl";
import { IFormActionComponent } from "../../Structure/IFormAction";
import { PropertyEditorBoolean } from "../../../CommonUI/PropertyEditing/PropertyEditorBoolean";
import { DataUtilities } from "../../../rule-engine-core/Common/DataUtilities";
import { UIStyleConstants } from "../../../rule-engine-core/Common/UIStyleConstants";
import { PropertyEditorCollapse } from "../../../CommonUI/PropertyEditing/PropertyEditorCollapse";
import { UISpacedBoxContent } from "../../../CommonUI/BuildlingBlocks/UISpacedBoxContent";

export enum IXButtonType {
    "BUTTON" = "button",
    "LINK" = "link",
    "IMAGE" = "image",
}

class XButtonEditUI extends React.Component<{ editMe: ButtonFormControl }> {
    render() {
        return (
            <UISpacedBoxContent>
                <PropertyEditorText
                    editObject={this.props.editMe}
                    propertyName={"label"}
                    label="label"
                />
                <PropertyEditorBoolean
                    editObject={this.props.editMe}
                    label="Use name as outcomeName"
                    propertyName={"useNameAsOutcomeName"}
                />

                <PropertyEditorCollapse
                    editObject={this.props.editMe}
                    collapseLogicOrField={"useNameAsOutcomeName"}
                    showIfFalse={true}
                >
                    <PropertyEditorText
                        editObject={this.props.editMe}
                        propertyName={"outcomeName"}
                        label="Outcome Name"
                    />
                </PropertyEditorCollapse>
                <PropertyEditorSelect
                    editObject={this.props.editMe}
                    propertyName={"size"}
                    label="Size"
                    options={[ButtonControlSize.SMALL, ButtonControlSize.STANDARD, ButtonControlSize.LARGE]}
                />
                <PropertyEditorSelect
                    editObject={this.props.editMe}
                    propertyName={"type"}
                    label="Type"
                    options={[IXButtonType.BUTTON, IXButtonType.LINK, IXButtonType.IMAGE]}
                />
                <PropertyEditorCollapse
                    editObject={this.props.editMe}
                    collapseLogicOrField={"type"}
                >
                    <PropertyEditorImage
                        editObject={this.props.editMe}
                        propertyName={"image"}
                        label="Image"
                    />
                </PropertyEditorCollapse>
            </UISpacedBoxContent>
        );
    }
}

export class ButtonFormControl extends FormControl implements IFormActionComponent {
    readonly __type: string = "Button";

    type: IXButtonType = IXButtonType.BUTTON;
    image?: string;
    label: string = "Button Label";
    size?: ButtonControlSize;

    useNameAsOutcomeName: boolean = true;
    outcomeName: string = "";

    onClick = () => {
        this.getFormContext()?.outcomeTriggered(this.getOutcomeName());
    };

    getOutcomeName = () => {
        if (this.useNameAsOutcomeName) return this.label;
        return this.outcomeName;
    };

    render() {
        switch (this.type) {
            case IXButtonType.BUTTON:
                return (
                    <StyledButton
                        variant="contained"
                        sx={{ background: UIStyleConstants.BLUE_COLOR }}
                        onClick={this.onClick}
                        size={this.size}
                        data-testid={`xbutton-button-${this.label}`}
                        ref={this.ref}
                    >
                        {this.label}
                    </StyledButton>
                );
            case IXButtonType.LINK:
                return (
                    <StyledLink
                        sx={{ background: UIStyleConstants.BLUE_COLOR }}
                        onClick={this.onClick}
                        size={this.size}
                        data-testid={`xbutton-link-${this.label}`}
                        ref={this.ref}
                    >
                        {this.label}
                    </StyledLink>
                );
            case IXButtonType.IMAGE:
                let height = 16;
                if (this.size === ButtonControlSize.SMALL) {
                    height = 12;
                } else if (this.size === ButtonControlSize.LARGE) {
                    height = 20;
                }
                return (
                    <IconButton
                        onClick={this.onClick}
                        data-testid={`xbutton-image-${this.label}`}
                        ref={this.ref}
                        size="large"
                    >
                        <img
                            src={this.image}
                            alt={this.label}
                            height={height}
                        />
                    </IconButton>
                );
            default:
                return null;
        }
    }

    renderEditUI(): JSX.Element | null {
        return <XButtonEditUI editMe={this} />;
    }

    getDesignValidationIssues(): ValidationIssue[] {
        return [];
    }

    getRuntimeValidationIssues(): ValidationIssue[] {
        return [];
    }

    getAddedData(): IDataDefinition | IDataDefinition[] | undefined {
        return;
    }

    getStorageData(): object {
        let retData = super.getStorageData();

        retData = DataUtilities.addStorageData(
            this,
            retData,
            "__type",
            "readonly",
            "type",
            "image",
            "label",
            "size",
            "useNameAsOutcomeName",
            "outcomeName",
        );

        return retData;
    }

    setStorageData(data: object): void {
        super.setStorageData(data);

        DataUtilities.setStorageData(
            this,
            data,
            "__type",
            "readonly",
            "type",
            "image",
            "label",
            "size",
            "useNameAsOutcomeName",
            "outcomeName",
        );
    }
}
